import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'
import { useDispatch } from 'react-redux'
import { signInWithCustomToken, signOut } from 'firebase/auth'
import { auth } from 'providers/firebaseAuth'
import { setSelectedLoginDetails } from 'slices/login/LoginPersistSlice'
import { useImpersonationSession } from 'providers/ImpersonationProvider'
import api from 'api'
import routes from 'router/routes'

// Firebase error codes that indicate the custom token couldn't be accepted —
// usually an environment configuration / signing-authorization problem rather
// than anything the operator did wrong.
const CONFIG_ERROR_CODES = new Set([
  'auth/invalid-custom-token',
  'auth/custom-token-mismatch',
  'auth/invalid-credential',
])

/**
 * Handoff landing page opened by the Tenant Manager when starting an impersonation session.
 *
 * URL params (all required):
 *   token      — Firebase custom token minted for the tenant's CRM Firebase project
 *   tenantId   — CRM Firebase tenant ID (set on auth before signing in)
 *   session    — TM backend session UUID (stored for banner / end-session)
 *   businessId — The tenant's hh_business UUID (set in Redux so the CRM loads correctly)
 *
 * Optional display params forwarded by TM:
 *   tenantName — human-readable tenant name shown in the banner
 *   mode       — 'view-only' | 'edit'
 *   tmOrigin   — origin of the TM workspace tab (for postMessage back on End Session)
 */
export default function ImpersonatePage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { setSession, clearSession } = useImpersonationSession()
  const [error, setError] = useState(null)
  const [details, setDetails] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    // The token is delivered in the URL fragment (not the query string) so it stays out of server
    // access logs and Referer headers. Fall back to the query param for any in-flight legacy links.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const token = hashParams.get('token') || params.get('token')
    const tenantId = params.get('tenantId')
    const sessionId = params.get('session')
    // No fallback to the session id: the session id is not a business id, and using it would set the
    // wrong selected-business in Redux. A missing businessId is treated as an invalid link below.
    const businessId = params.get('businessId')
    const tenantName = params.get('tenantName') || 'Unknown Tenant'
    const mode = params.get('mode') || 'view-only'
    const tmOrigin = params.get('tmOrigin') || ''

    if (!token || !tenantId || !sessionId || !businessId) {
      setDetails({
        step: 'validate-link',
        sessionId: sessionId || '(missing)',
        businessId: businessId || '(missing)',
        tenantName,
        firebaseTenantId: tenantId || '(missing)',
        tokenPresent: Boolean(token),
        at: new Date().toISOString(),
      })
      setError('This impersonation link is missing required information. Please start a new session from the Tenant Manager.')
      return
    }

    async function signIn() {
      try {
        auth.tenantId = tenantId
        await signInWithCustomToken(auth, token)

        setSession({ sessionId, tenantName, mode, tmOrigin, startedAt: new Date().toISOString() })

        dispatch(setSelectedLoginDetails({ show: false, businessName: tenantName, businessId }))
        dispatch(api.util.resetApiState())

        navigate(routes.app.recentlyViewed, { replace: true })
      } catch (e) {
        // Clean up any partial state so a failed attempt doesn't leave a stale
        // impersonation session or signed-in Firebase user lingering in this tab.
        auth.tenantId = null
        clearSession()
        signOut(auth).catch(() => {})

        setDetails({
          step: 'firebase-sign-in',
          errorCode: e?.code || 'unknown',
          errorMessage: e?.message || String(e),
          sessionId,
          businessId,
          tenantName,
          firebaseTenantId: tenantId,
          mode,
          at: new Date().toISOString(),
        })
        if (CONFIG_ERROR_CODES.has(e?.code)) {
          setError('Impersonation is not configured correctly for this environment, so the session could not be started. Please contact support.')
        } else {
          setError('Failed to start the impersonation session. Please contact support.')
        }
      }
    }

    signIn()
  }, [dispatch, navigate, setSession, clearSession])

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, height: '100vh', fontFamily: 'sans-serif', padding: 24, textAlign: 'center' }}>
        <p style={{ color: '#b91c1c', maxWidth: 460, margin: 0 }}>{error}</p>
        {details && (
          <div
            style={{
              textAlign: 'left',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '12px 16px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              color: '#4b5563',
              maxWidth: 460,
              width: '100%',
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#374151' }}>Support details</div>
            {Object.entries(details).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', gap: 8, wordBreak: 'break-all' }}>
                <span style={{ color: '#9ca3af', minWidth: 110 }}>{key}</span>
                <span>{String(value)}</span>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={() => window.close()}
          style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, color: '#374151', padding: '6px 16px', fontSize: 13, cursor: 'pointer' }}
        >
          Close window
        </button>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'sans-serif' }}>
      <p style={{ color: '#6b7280' }}>Starting impersonation session…</p>
    </div>
  )
}
